'use client';
import React, { useEffect, useMemo, useState } from 'react';
import FadeInSection from './FadeInSection';

/* ---------- GA (local only) ---------- */
function ga(event: string, params?: Record<string, unknown>) {
  try {
    if (typeof window === 'undefined') return;
    if (window.top !== window.self) return; // parent-only iframes: keep this
    const w: any = window;
    if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({ event, ...(params || {}) });
      return;
    }
    // Fallback if gtag is still present in older deploys
    if (typeof w.gtag === 'function') {
      w.gtag('event', event, params || {});
    }
  } catch {}
}


/* ---------- WP copy endpoint ---------- */
const WP_MARKETING_ENDPOINT = 'https://wateredgetech.com/wp-json/wet/v1/marketing';

/* ---------- Types ---------- */
type StepKey = 'inlet' | 'exchange' | 'outlet';
type WpStep = { number?: string; title?: string; description?: string; list?: string[] };
type Step = { title: string; content: string; key: StepKey; list: string[] };

/* ---------- Defaults ---------- */
const DEFAULT_STEPS: Step[] = [
  {
    title: 'Step 1: Hard Water Enters',
    key: 'inlet',
    content: 'Hardness ions ride the flow into the resin bed.',
    list: [
      'Main line feeds the control valve and tank.',
      'Flow carries hardness ions into the resin bed.',
    ],
  },
  {
    title: 'Step 2: Ion Exchange',
    key: 'exchange',
    content: 'Resin captures Ca²⁺/Mg²⁺. Charge activity is visible around beads.',
    list: [
      'Resin sites capture Ca²⁺/Mg²⁺.',
      'Charge activity indicates ion exchange.',
    ],
  },
  {
    title: 'Step 3: Soft Water Out',
    key: 'outlet',
    content: 'Soft water rises through the riser and exits to the home.',
    list: [
      'Soft water rises via the riser tube.',
      'Outlet line delivers soft water to fixtures.',
    ],
  },
];

const HowItWorks: React.FC = () => {
  // Copy overrideable via WP
  const [heading, setHeading] = useState<string>('How a water softener works (ion exchange)');
  const [sub, setSub] = useState<string>('Inside a water softener, resin swaps calcium and magnesium for sodium so every tap delivers soft water.');
  const [steps, setSteps] = useState<Step[]>(DEFAULT_STEPS);
  const [regenerationNote, setRegenerationNote] = useState('Regeneration with brine recharges the resin periodically.');

  // Fetch WP copy overrides (sections.how_*)
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch(WP_MARKETING_ENDPOINT, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          signal: ctrl.signal,
        });
        if (!r.ok) return;
        const data = await r.json();
        const s = data?.sections || {};
        if (cancelled) return;

        if (typeof s.how_heading === 'string' && s.how_heading.trim()) setHeading(s.how_heading.trim());
        if (typeof s.how_sub === 'string' && s.how_sub.trim()) setSub(s.how_sub.trim());
        if (typeof s.how_regeneration_note === 'string' && s.how_regeneration_note.trim()) setRegenerationNote(s.how_regeneration_note.trim());

        const wpSteps = (s.how_steps as WpStep[] | undefined)?.filter(Boolean) || [];
        if (wpSteps.length) {
          const mapped = wpSteps.slice(0, 3).map((w, i) => {
            const key: StepKey = (['inlet', 'exchange', 'outlet'][i] as StepKey) ?? 'inlet';
            const n = (w.number || '').trim();
            const title = [n, (w.title || '').trim()].filter(Boolean).join(' ').trim() || DEFAULT_STEPS[i]?.title;
            const content = (w.description || '').trim() || DEFAULT_STEPS[i]?.content;
            const list = Array.isArray(w.list) && w.list.every(item => typeof item === 'string')
              ? w.list.filter(item => item.trim()).map(item => item.trim())
              : DEFAULT_STEPS[i]?.list;
            return { key, title, content, list };
          });
          setSteps(mapped);
        }
      } catch {
        /* keep defaults */
      }
    })();
    return () => { cancelled = true; ctrl.abort(); };
  }, []);

  const [active, setActive] = useState<StepKey>('inlet');

  // Fire a single view event when the section mounts
  useEffect(() => {
    ga('section_view', { section: 'how_it_works_wet' });
  }, []);

  const onSelectStep = (key: StepKey, title: string) => {
    setActive(key);
    ga('select_content', {
      content_type: 'how_step',
      item_id: key,
      label: title,
      section: 'how_it_works_wet',
    });
  };

  const colors = useMemo(
    () => ({
      water: '#60a5fa',
      waterSoft: '#7dd3fc',
      hardIon: '#f59e0b',
      beadIdle: '#334155',
      beadCharged: '#f59e0b',
      spark: '#fde68a',
    }),
    []
  );

  return (
    <section
      id="how-it-works"
      // Added 'anchor-target' class here
      className="py-24 bg-slate-800/30 anchor-target"
    >
      <FadeInSection>
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold mb-4 text-center">{heading}</h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-12 text-center">
            {sub}
          </p>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
            {/* Animated Diagram */}
            <div className="bg-slate-900/40 rounded-xl p-4 md:p-6">
              <div className="relative">
                <style>{`
                  @keyframes bubbleUp { 
                    0% { transform: translateY(0); opacity: .9; } 
                    100% { transform: translateY(-140px); opacity: 0; } 
                  }
                  @keyframes flowRight {
                    0% { transform: translateX(0); opacity: .7; }
                    100% { transform: translateX(120px); opacity: 0; }
                  }
                  @keyframes flicker {
                    0%, 100% { opacity: .15 }
                    40% { opacity: .9 }
                    60% { opacity: .4 }
                  }
                  @keyframes glow {
                    0%, 100% { filter: drop-shadow(0 0 0 rgba(125,211,252,0)); }
                    50% { filter: drop-shadow(0 0 12px rgba(125,211,252,0.7)); }
                  }
                `}</style>

                <svg viewBox="0 0 520 420" className="w-full h-auto" role="img" aria-label="Water softener ion exchange diagram">
                  {/* Background */}
                  <defs>
                    <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                      <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#0f172a" strokeWidth="1"/>
                    </pattern>
                    <linearGradient id="tankGrad" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#0b1220"/>
                      <stop offset="100%" stopColor="#0a0f1a"/>
                    </linearGradient>
                    <linearGradient id="waterGrad" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.35"/>
                      <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.15"/>
                    </linearGradient>
                  </defs>
                  <rect x="0" y="0" width="520" height="420" fill="url(#grid)" />

                  {/* Tank */}
                  <g transform="translate(110,30)">
                    {/* Body */}
                    <rect x="0" y="0" width="220" height="360" rx="28" fill="url(#tankGrad)" stroke="#1f2937" strokeWidth="2"/>
                    {/* Resin cavity */}
                    <rect x="16" y="80" width="188" height="248" rx="18" fill="#0b1220" stroke="#1e293b" strokeWidth="2"/>
                    <rect x="16" y="80" width="188" height="248" rx="18" fill="url(#waterGrad)" />

                    {/* Riser */}
                    <rect x="102" y="40" width="16" height="300" rx="8" fill="#0f172a" stroke="#1e293b" strokeWidth="2" />

                    {/* Inlet pipe (left) */}
                    <g transform="translate(-70,80)">
                      <rect x="0" y="20" width="86" height="14" rx="7" fill="#0f172a" stroke="#1e293b" strokeWidth="2" />
                      { (active === 'inlet' || active === 'exchange') && [0,1,2,3].map(i => (
                        <circle
                          key={i}
                          cx={12 + i*20}
                          cy={27}
                          r="4"
                          fill={colors.water}
                          style={{ animation: `flowRight 1.2s ${i*0.15}s linear infinite` }}
                        />
                      ))}
                    </g>

                    {/* Outlet pipe (right, top) */}
                    <g transform="translate(220,-6)">
                      <rect x="0" y="46" width="86" height="14" rx="7" fill="#0f172a" stroke="#1e293b" strokeWidth="2" />
                      { active === 'outlet' && [0,1,2,3].map(i => (
                        <circle
                          key={i}
                          cx={6 + i*18}
                          cy={53}
                          r="3.6"
                          fill={colors.waterSoft}
                          style={{ animation: `flowRight 1.1s ${i*0.12}s linear infinite` }}
                        />
                      ))}
                      <circle
                        cx="95"
                        cy="53"
                        r="8"
                        fill={colors.waterSoft}
                        style={{ animation: active === 'outlet' ? 'glow 1.4s ease-in-out infinite' : 'none', opacity: active === 'outlet' ? 1 : 0 }}
                      />
                    </g>

                    {/* Head valve */}
                    <rect x="40" y="-18" width="144" height="46" rx="10" fill="#0f172a" stroke="#1e293b" strokeWidth="2" />

                    {/* Resin beads grid */}
                    <g aria-label="Resin beads">
                      {Array.from({ length: 12 }).map((_, row) =>
                        Array.from({ length: 6 }).map((__, col) => {
                          const x = 28 + col * 30;
                          const y = 96 + row * 20;
                          const delay = (row * 6 + col) * 0.03;
                          const sparkD = `M ${x-7} ${y-8} L ${x-3} ${y-4} L ${x-7} ${y} L ${x-3} ${y+4} L ${x-7} ${y+8}`;

                          return (
                            <g key={`${row}-${col}`}>
                              <circle
                                cx={x}
                                cy={y}
                                r="6.5"
                                fill={active === 'exchange' ? colors.beadCharged : colors.beadIdle}
                                stroke="#0b1220"
                                strokeWidth="1.5"
                              />
                              {(active === 'inlet' || active === 'exchange') && (
                                <circle
                                  cx={x - 16}
                                  cy={y + 8}
                                  r="3.2"
                                  fill={colors.hardIon}
                                  style={{ animation: `flowRight 1.4s ${delay}s linear infinite` }}
                                />
                              )}
                              {active === 'exchange' && (
                                <path
                                  d={sparkD}
                                  fill="none"
                                  stroke={colors.spark}
                                  strokeWidth="1.6"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  style={{ animation: `flicker 1.2s ${delay}s ease-in-out infinite` }}
                                />
                              )}
                            </g>
                          );
                        })
                      )}
                    </g>

                    {/* Riser bubbles: only Step 3 */}
                    {active === 'outlet' && Array.from({ length: 10 }).map((_, i) => (
                      <circle
                        key={i}
                        cx={110}
                        cy={320}
                        r={2.8}
                        fill={colors.waterSoft}
                        style={{ animation: `bubbleUp 1.8s ${i * 0.15}s linear infinite` }}
                      />
                    ))}
                  </g>

                  {/* Labels */}
                  <g fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI" fontSize="12" fill="#94a3b8">
                    <text x="24" y="110">Hard water in</text>
                    <text x="362" y="60">Soft water out</text>
                    <text x="132" y="70">Resin bed</text>
                    <text x="190" y="46">Valve</text>
                    <text x="210" y="352">Riser tube</text>
                  </g>
                </svg>
              </div>
              <p className="sr-only">Inlet flow, ion exchange with charge, and outlet soft water stages.</p>
            </div>

            {/* Stepper */}
            <div>
              <div className="space-y-4">
                {steps.map(({ key, title, content, list }, idx) => {
                  const isActive = active === key;
                  return (
                    <div key={key}>
                      <button
                        type="button"
                        onClick={() => onSelectStep(key, title)}
                        className={[
                          'w-full text-left p-4 border-l-4 rounded-r-lg transition-colors',
                          isActive ? 'border-teal-500 bg-slate-700/50' : 'border-gray-600 bg-slate-800 hover:bg-slate-700/40'
                        ].join(' ')}
                        aria-expanded={isActive}
                        aria-controls={`step-${key}`}
                      >
                        <h3 className="font-semibold text-lg">{title}</h3>
                      </button>
                      {isActive && (
                        <div id={`step-${key}`} className="pl-6 pt-2 text-gray-300">
                          <p className="mt-1">{content}</p>
                          {list && list.length > 0 && (
                            <ul className="list-disc pl-5 mt-2 space-y-1">
                              {list.map((item, itemIdx) => (
                                <li key={itemIdx}>{item}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-8 text-sm text-gray-400">{regenerationNote}</div>
            </div>
          </div>
        </div>
      </FadeInSection>
    </section>
  );
};

export default HowItWorks;
